/* ci-discovery.js — Rebuy-style "product discovery" click-through attribution
   (fast-stack variant, 2026-09-07).

   Rebuy's own mechanism (reverse-engineered from the bundles Rebuy serves at
   cdn.rebuyengine.com/onsite/js/ — global-*.js, fetched 2026-09-07 against
   this shop): a capture-time click on a recommendation-widget product link
   calls Tracker.recordDiscoveryProduct(handle, {widget: widgetId}), which
   writes window.localStorage['_rdiscovery'] = JSON.stringify({
     [handle]: { widget: widgetId, timestamp: Date.now() }, ...
   }) — one JSON blob keyed by product HANDLE. A companion URL-param channel
   (?_rdiscovery-handle=<handle>&_rdiscovery-widget=<id>) is appended to the
   SAME outbound link as a fallback for storage-partitioned browsers; the
   landing page reads and re-records it, then strips the params via
   history.replaceState. Lifetime is a fixed 24h window: Tracker.init() sets
   `discovery_expires = Date.now() - 864e5` (86,400,000ms) once per page load,
   and a stored entry is only honoured while its timestamp is newer than that
   cutoff — expired entries are deleted on read. On the product page,
   Tracker.manageDiscoveryProduct() looks up the CURRENT product by handle; if
   a live entry exists it builds a small properties object
   (_source, _attribution: "Product Discovery", _widget_id, ...) and appends
   ONE hidden <input name="properties[x]"> per key directly onto
   `form[action="/cart/add"]` at load time — no submit handler, no fetch
   interception, no observer.

   This variant mirrors that mechanism with our own property names and
   surfaces, keyed by numeric product ID (our recommendation markup already
   carries ids, not handles), same 24h lifetime, same "hidden inputs appended
   once at page load" delivery. It never touches the buy button or a submit
   handler (shopify-theme-dev skill hard rule 3).

   Surfaces instrumented (2026-09-07): the native cart-drawer "Others also
   bought" rows (source drawer-native), the PDP "Frequently bought together"
   grid (pdp-related-grid), the cart-page grid (cart-page-grid). Each surface
   marks its outer container with data-ci-disc-source / data-ci-disc-host /
   data-ci-disc-intent, and each recommended item's wrapper with
   data-ci-disc-product / data-ci-disc-rank. The PDP "Complete the ritual"
   upsell card (snippets/product-upsell-card.liquid) does NOT navigate on its
   "More info" tap — it opens an in-page <template> modal — so it carries no
   click-through attribution; its existing "card" source stays a direct-add-
   only tag (see assets/product-upsell.js), unchanged by this file.
*/
(function () {
  var STORAGE_KEY = 'ci_discovery_attribution';
  var TTL_MS = 24 * 60 * 60 * 1000; // 24h — matches Rebuy's measured discovery_expires window

  function readStore() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  }

  function writeStore(store) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch (e) {
      /* storage unavailable (private mode, quota) — attribution is best-effort */
    }
  }

  /* (a) Capture-phase click: record attribution the moment a shopper clicks
     into a recommendation surface, before navigation happens. Any click
     bubbling through a [data-ci-disc-source] container on an <a> whose
     nearest data-ci-disc-product ancestor names a recommended product id. */
  document.addEventListener('click', function (evt) {
    var target = evt.target;
    if (!target || !target.closest) return;
    var link = target.closest('a');
    if (!link) return;
    var container = link.closest('[data-ci-disc-source]');
    if (!container) return;
    var item = link.closest('[data-ci-disc-product]');
    if (!item) return;
    var product = item.getAttribute('data-ci-disc-product');
    if (!product) return;

    var store = readStore();
    store[String(product)] = {
      source: container.getAttribute('data-ci-disc-source') || '',
      host: container.getAttribute('data-ci-disc-host') || '',
      intent: container.getAttribute('data-ci-disc-intent') || '',
      rank: item.getAttribute('data-ci-disc-rank') || '',
      ts: Date.now()
    };
    writeStore(store);
  }, true);

  /* (b) On the landing product page, if a live (non-expired) attribution
     exists for THIS product, append hidden properties[] inputs to every
     add-to-cart form once at load. No submit handler, no observer, no
     buy-button code. */
  function applyDiscoveryAttribution() {
    var meta = (window.ShopifyAnalytics && window.ShopifyAnalytics.meta) || {};
    var productId = meta.product && meta.product.id;
    if (!productId) return;

    var store = readStore();
    var key = String(productId);
    var entry = store[key];
    if (!entry) return;

    if (typeof entry.ts !== 'number' || (Date.now() - entry.ts) > TTL_MS) {
      delete store[key];
      writeStore(store);
      return;
    }

    var props = {
      _upsell_source: entry.source || '',
      _upsell_host: entry.host || '',
      _upsell_rank: entry.rank || '',
      _upsell_intent: entry.intent || '',
      _upsell_attribution: 'discovery'
    };

    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    for (var i = 0; i < forms.length; i++) {
      for (var name in props) {
        if (!props.hasOwnProperty(name)) continue;
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[' + name + ']';
        input.value = props[name];
        forms[i].appendChild(input);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyDiscoveryAttribution);
  } else {
    applyDiscoveryAttribution();
  }
})();
